import { FC, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Grid, Typography } from '@mui/material';
import { IEmailTemplateDetails, IEmailTemplateSection, ITemplateInputProps } from '../../../models';
import { numberSortAsc, scrollToTop } from '../../../helpers';
import { TextboxInput, EditorInput } from './template-control';
import { Close, Save } from '@mui/icons-material';
import { putEmailTemplate } from '../../../fetch';
import { Loader } from '../../../components';
import { deepEqual } from 'fast-equals';

interface IEditEmailTemplateFormProps {
  emailTemplateDetails: IEmailTemplateDetails;
  onSubmit?: () => void;
}

export const EditEmailTemplateForm: FC<IEditEmailTemplateFormProps> = ({
  emailTemplateDetails,
  onSubmit
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [values, setValues] = useState<IEmailTemplateSection[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  useEffect(() => {
    const initialValues = emailTemplateDetails.templateSections.map(section => {
      return { ...section, touched: false };
    });
    setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTemplateDetails]);

  const handleChange = async (value: string, editableField: IEmailTemplateSection) => {
    let copy = [...values];
    const itemIndex = values.findIndex(
      item => item.emailTemplateSectionId === editableField.emailTemplateSectionId
    );

    // Guard against undefined error if index is not found
    if (itemIndex === -1) {
      return;
    }

    const hasChanges = emailTemplateDetails.templateSections[itemIndex].sectionTemplate !== value;
    //Item will always exist in values array, so update value and set to touched
    copy[itemIndex] = {
      ...copy[itemIndex],
      sectionTemplate: value,
      touched: hasChanges
    };
    setValues(copy);

    // Check against original values to see if there are any differences
    const compare = emailTemplateDetails.templateSections.map(val => {
      return { ...val, touched: false };
    });
    if (!deepEqual(compare, copy)) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const data = values
      .filter(val => val.touched)
      .map(val => {
        return {
          emailTemplateSectionId: val.emailTemplateSectionId,
          sectionName: val.sectionName,
          sectionTemplate: val.sectionTemplate,
          sortOrder: val.sortOrder
        };
      });

    try {
      await putEmailTemplate(emailTemplateDetails.emailTemplateId, data);
      enqueueSnackbar(`Email template updated!`, {
        variant: 'success'
      });
      onSubmit && onSubmit(); // callback for refetching data, if needed
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error updating email template, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (values.filter(val => val.touched)?.length > 0) {
      const result = window.confirm('You have unsaved changes, are you sure you want to reset?');
      if (result) {
        setValues(emailTemplateDetails.templateSections);
        setHasChanges(false);
        scrollToTop();
      } else {
        return;
      }
    }
  };

  const renderEditableField = (editableField: IEmailTemplateSection) => {
    const value =
      values.find(val => val.sectionName === editableField.sectionName)?.sectionTemplate ?? '';
    let TemplateInput: FC<ITemplateInputProps> | undefined;

    if (
      editableField.sectionName === 'Subject' ||
      editableField.sectionName === 'HeaderLabel' ||
      editableField.sectionName === 'HeaderData'
    ) {
      TemplateInput = TextboxInput;
    } else {
      TemplateInput = EditorInput;
    }
    if (!TemplateInput) {
      return null;
    }
    return <TemplateInput input={editableField} value={value} onChange={handleChange} />;
  };
  return (
    <form>
      {isSubmitting && <Loader type="fullscreen" position="centered" title="Saving..." />}
      <Grid container>
        <Grid item xs={12} xl={8}>
          <Typography mb={2}>
            Certain templates utilize tokens to inject dynamic content into sections of a template
            (i.e. @@TokenName@@). Do NOT delete these tokens as this will break certain
            functionality.
          </Typography>
          <Grid container spacing={2}>
            {numberSortAsc(emailTemplateDetails.templateSections, 'sortOrder').map(section => {
              return (
                <Grid item xs={12} key={section.sectionName}>
                  {renderEditableField(section)}
                </Grid>
              );
            })}
          </Grid>
          <Box mt={2} mb={2}>
            <Button
              disabled={isSubmitting || !hasChanges}
              type="button"
              startIcon={<Save />}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              color="inherit"
              disabled={isSubmitting || !hasChanges}
              startIcon={<Close />}
              onClick={handleReset}
            >
              Cancel
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};
